export const SKILL_ACQUISITION_TYPES = [
  'Communication Skills',
  'Social Skills',
  'Daily Living Skills',
  'Academic Skills',
  'Fine Motor Skils',
  'Gross Motor Skills',
  'Sensory Processing Skills',
  'Cognitive Skills',
  'Play & Leisure Skills',
  'Self-Advocacy Skills',
  'Vocational Skills',
  'Other Skills',
];

export const BEHAVIOR_TYPE_CATEGORIES = [
  'Self-Regulation',
  'Adaptive Behavior',
  'Reduction of Challenging Behaviors',
  'Impulse Control',
  'Social-Emotional Behavior',
  'Other Behavior',
];

export const CATEGORY_TOOLTIPS = {
  ABA: 'Applied Behavioral Analysis',
  OT: 'Occupational Therapy',
  PT: 'Physical Therapy',
  APE: 'Adaptive Physical Education',
};

export const PROMPTING_TOOLTIPS = {
  I: 'Independent',
  VP: 'Verbal Prompt',
  IVP: 'Indirect Verbal Prompt',
  DVP: 'Direct Verbal Prompt',
  PVP: 'Partial Verbal Prompt',
  GP: 'Gestural Prompt',
  PP: 'Physical Prompt',
  ViP: 'Visual Prompt',
  M: 'Modeling',
};

export const ASSIST_VERBAL_TOOLTIP = [
  {
    key: 'total_assist',
    label: 'Total Assist',
    tooltip: 'Staff expends >75% of effort (client performs <25% of task)',
  },
  {
    key: 'maximal_assist',
    label: 'Maximal Assist',
    tooltip: 'Staff expends 50–75% of effort (client performs 25–50% of task)',
  },
  {
    key: 'minimal_contact_assist',
    label: 'Minimal Contact Assist',
    tooltip: 'Staff expends 25–50% of effort (client performs 50–75% of task)',
  },
  {
    key: 'moderate_assist',
    label: 'Moderate Assist',
    tooltip: 'Staff expends no more than 25% of effort (client performs 75%+ of task)',
  },
  {
    key: 'independent',
    label: 'Independent',
    tooltip: 'No physical assistance from staff; client performs task entirely',
  },
  {
    key: 'minimal_verbal',
    label: 'Minimal Verbal Prompting',
    tooltip: '1–2 verbal prompts required to initiate or complete task',
  },
  {
    key: 'moderate_verbal',
    label: 'Moderate Verbal Prompting',
    tooltip: '3–4 verbal prompts required to initiate or complete task',
  },
  {
    key: 'maximum_verbal',
    label: 'Maximum Verbal Prompting',
    tooltip: '5 or more verbal prompts required to initiate or complete task',
  },
];

// Tooltips for prompting-based options
export const PROMPTING_TOOLTIPS_LIST = [
  {
    key: 'verbal-minimal',
    label: 'Verbal → Minimal',
    tooltip: '1–2 verbal prompts required to initiate or complete task',
  },
  {
    key: 'verbal-moderate',
    label: 'Verbal → Moderate',
    tooltip: '3–4 verbal prompts required to initiate or complete task',
  },
  {
    key: 'verbal-maximum',
    label: 'Verbal → Maximum',
    tooltip: '5 or more verbal prompts required to initiate or complete task',
  },
  {
    key: 'permanent-visual',
    label: 'Permanent Visual Prompting',
    tooltip: 'Visual cues that remain in place to guide task completion (e.g., written instructions, picture schedules)',
  },
  {
    key: 'visual',
    label: 'Visual Prompting',
    tooltip: 'Temporary visual cues provided to guide task completion (e.g., pointing, showing a picture)',
  },
  {
    key: 'gestural',
    label: 'Gestural Prompting',
    tooltip: 'Non-verbal gestures used to guide behavior (e.g., pointing, nodding, hand signals)',
  },
  {
    key: 'modeling',
    label: 'Modeling',
    tooltip: 'Demonstrating the desired behavior for the client to observe and imitate',
  },
  {
    key: 'positional',
    label: 'Positional Prompting',
    tooltip: 'Arranging materials or environment to guide correct response (e.g., placing correct item closer)',
  },
  {
    key: 'independent',
    label: 'Independent',
    tooltip: 'No prompts required; client performs task entirely on their own',
  },
  {
    key: 'tactile',
    label: 'Tactile Prompting',
    tooltip: 'Physical touch or tactile cues used to guide behavior (e.g., tapping shoulder, guiding hand)',
  },
];

// Tooltips for physical assistance-based options
export const PHYSICAL_ASSISTANCE_TOOLTIPS = [
  {
    key: 'physical-full-total-assist',
    label: 'Physical → Full → Total Assist (75+% effort)',
    tooltip: 'Staff expends >75% of effort (client performs <25% of task)',
  },
  {
    key: 'physical-full-maximal-assist',
    label: 'Physical → Full → Maximal Assist',
    tooltip: 'Staff expends 50–75% of effort (client performs 25–50% of task)',
  },
  {
    key: 'physical-partial-moderate',
    label: 'Physical → Partial → Moderate',
    tooltip: 'Staff expends no more than 25% of effort (client performs 75%+ of task)',
  },
  {
    key: 'physical-partial-minimal',
    label: 'Physical → Partial → Minimal',
    tooltip: 'Staff expends 25–50% of effort (client performs 50–75% of task)',
  },
  {
    key: 'physical-independent',
    label: 'Physical → Independent',
    tooltip: 'Staff expends 0% of effort (client performs 100% of task)',
  },
];

export const TASK_ANALYSIS_PROMPTING_OPTIONS = {
  'I, VP (IVP, DVP, PVP), GP, PP, ViP, M': [
    { value: 'independent', label: 'Independent' },
    { value: 'physical', label: 'Physical' },
    { value: 'model', label: 'Model' },
    { value: 'visual', label: 'Visual' },
    { value: 'verbal', label: 'Verbal' },
    { value: 'gestural', label: 'Gestural' },
  ],
  OT: [
    {
      value: 'physical-full-total-assist',
      label: 'Physical → Full → Total Assist (75+% effort)',
    },
    {
      value: 'physical-full-maximal-assist',
      label: 'Physical → Full → Maximal Assist',
    },
    {
      value: 'physical-partial-moderate',
      label: 'Physical → Partial → Moderate',
    },
    {
      value: 'physical-partial-minimal',
      label: 'Physical → Partial → Minimal',
    },
    { value: 'verbal-minimal', label: 'Verbal → Minimal' },
    { value: 'verbal-moderate', label: 'Verbal → Moderate' },
    { value: 'verbal-maximum', label: 'Verbal → Maximum' },
    {
      value: 'permanent-visual-prompting',
      label: 'Permanent Visual Prompting',
    },
    { value: 'visual-prompting', label: 'Visual Prompting' },
    { value: 'gestural-prompting', label: 'Gestural Prompting' },
    { value: 'modeling', label: 'Modeling' },
    { value: 'positional-prompting', label: 'Positional Prompting' },
    { value: 'independent', label: 'Independent' },
  ],
};

export const physicalAssistanceBased = [
  'physical-full-total-assist',
  'physical-full-maximal-assist',
  'physical-partial-moderate',
  'physical-partial-minimal',
  'physical-independent',
];

export const promptingBased = [
  'verbal-minimal',
  'verbal-moderate',
  'verbal-maximum',
  'permanent-visual',
  'visual',
  'gestural',
  'modeling',
  'positional',
  'independent',
  'tactile',
];

// Mapping from value to display label
export const PROMPTING_LEVEL_LABELS = {
  'physical-full-total-assist': 'Physical → Full → Total Assist (75+% effort)',
  'physical-full-maximal-assist': 'Physical → Full → Maximal Assist',
  'physical-partial-moderate': 'Physical → Partial → Moderate',
  'physical-partial-minimal': 'Physical → Partial → Minimal',
  'physical-independent': 'Physical → Independent',
  'verbal-minimal': 'Verbal → Minimal',
  'verbal-moderate': 'Verbal → Moderate',
  'verbal-maximum': 'Verbal → Maximum',
  'permanent-visual': 'Permanent Visual Prompting',
  'visual': 'Visual Prompting',
  'gestural': 'Gestural Prompting',
  'modeling': 'Modeling',
  'positional': 'Positional Prompting',
  'independent': 'Independent',
  'tactile': 'Tactile Prompting',
};